import { Box, Typography, useTheme } from "@outschool/backpack";
import {
  Leader,
  TeacherSearchSuggestions,
} from "@outschool/gql-backend-generated";
import { useTranslation } from "@outschool/localization";
import { leaderPath } from "@outschool/routes";
import { UserHeadshotImage } from "@outschool/ui-components-shared";
import { SimpleRatings } from "@outschool/ui-components-website";
import {
  Carousel,
  useImpressionTracking,
} from "@outschool/ui-legacy-component-library";
import { useIsMobile } from "@outschool/ui-utils";
import React from "react";

import Link from "../Link";

export default function TeacherSuggestions({
  teacherSuggestions,
  searchUid,
}: {
  teacherSuggestions: TeacherSearchSuggestions;
  searchUid?: string;
}) {
  const { teachers, term } = teacherSuggestions;
  const { t } = useTranslation(
    "client\\components\\teacher\\TeacherSuggestion",
  );
  const isMobile = useIsMobile();
  const impressionTrackingNode = null;

  useImpressionTracking({
    node: impressionTrackingNode,
    trackingLabel: "teacher_search_carousel_suppressed",
    uniqueId: "teacher_search_carousel_suppressed",
    sharedProperties: {
      teachers_max: 10,
      teachers_total: teachers.length,
      searchUid,
    },
  });

  const teacherCards = teachers.map((teacher, index) => (
    <TeacherCard
      teacher={teacher}
      isMobile={isMobile}
      key={teacher.uid}
      index={index}
      isSearchByName={!term}
      searchUid={searchUid}
    />
  ));

  return (
    <Box
      sx={{
        width: "100%",
      }}
    >
      {term && (
        <Typography
          sx={{
            paddingX: isMobile ? "1em" : 0,
          }}
          variant="h4"
        >
          {t("{{suggestionTerm}} Teachers", { suggestionTerm: term })}
        </Typography>
      )}
      <Box>
        <Carousel
          innerSx={{ justifyContent: "flex-start" }}
          sx={{ marginTop: "medium", justifyContent: "flex-start" }}
          arrowClickOnBox={true}
          arrowSx={{
            border: "1px solid",
            borderColor: "border",
            borderRadius: "100%",
            boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.25)",
            height: "32px",
            width: "32px",
            "& > button": {
              height: "32px",
              width: "32px",
            },
            maskImage: "",
            cursor: "pointer",
            opacity: "90%",
            textAlign: "center",
            color: "#585F69",
            margin: "0 auto",
            padding: "0px 0px 0px 0px",
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <Box
            flex
            sx={{
              paddingLeft: "1rem",
              paddingRight: "1rem",
              gap: "16px",
            }}
          >
            {teacherCards}
          </Box>
        </Carousel>
      </Box>
    </Box>
  );
}

const TeacherCard = ({
  teacher,
  isMobile,
  index,
  isSearchByName,
  searchUid,
}: {
  teacher: Leader;
  isMobile: boolean;
  index: number;
  isSearchByName: boolean;
  searchUid?: string;
}) => {
  const {
    uid: teacher_uid,
    leader_link,
    averageActivityStarRating: teacher_rating,
    reviewCount: review_count,
    name: teacher_name,
  } = teacher;

  const sharedProperties = {
    teacher_uid,
    teacher_name,
    teacher_rating,
    review_count,
    index,
    search_by_name: isSearchByName,
    searchUid,
  };
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        minHeight: 50,
        margin: "0 5px 2px 5px",
        width: isMobile ? "233px" : "365px",
        boxShadow: "0px 2px 2px 0px rgba(0, 0, 0, 0.08)",
        border: "2px solid",
        borderColor: theme.palette.neutral[200],
        borderRadius: "24px",
        "&:hover": {
          borderColor: theme.palette.primary[400],
        },
        "&:focus, &:focus-visible, &:focus-within": {
          borderColor: theme.palette.primary.dark,
        },
        "&:active": {
          borderColor: theme.palette.primary.main,
        },
      }}
    >
      <Box
        component={Link}
        to={leaderPath(teacher_uid, leader_link)}
        trackingName="teacher_search_carousel"
        trackingUniqueId={teacher_uid}
        sharedProperties={sharedProperties}
        trackViews={true}
        sx={{
          display: "flex",
          width: "100%",
          height: "100%",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          "&:focus, &:focus-visible": {
            outline: "none",
          },
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        <UserHeadshotImage user={teacher} size={104} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: isMobile ? "center" : "start",
            marginLeft: isMobile ? undefined : "1em",
            textAlign: isMobile ? "center" : "start",
          }}
        >
          <Box
            sx={{
              textOverflow: "ellipsis",
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: "1px",
              WebkitBoxOrient: "vertical",
              fontSize: "1.6rem",
              lineHeight: "24px",
              fontWeight: "500",
              marginTop: "0.5em",
              color: "#000000",
              fontStyle: "normal",
            }}
          >
            <Typography variant="inherit">{teacher_name}</Typography>
          </Box>
          <Box
            flex
            sx={{
              alignItems: "center",
              marginTop: "0.5em",
              gap: "8px",
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            {review_count > 0 && (
              <React.Fragment>
                <SimpleRatings
                  reviewCount={review_count}
                  starRating={teacher_rating}
                />
              </React.Fragment>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
